import { Coordinate, TileSize } from "../models"
import { fineToRoughCoordinates, incrementViaDirection, radiansToCardinalDirection } from "../util"

export const facingPlacementTarget = (location: Coordinate, angle: number): Coordinate => {
    const target = fineToRoughCoordinates(location)
    incrementViaDirection(target, radiansToCardinalDirection(angle), 1)
    return target
}

export const clampPlacementTarget = (origin: Coordinate, target: Coordinate, range: number): Coordinate => ({
    x: Math.max(origin.x - range, Math.min(origin.x + range, target.x)),
    y: Math.max(origin.y - range, Math.min(origin.y + range, target.y)),
})

export const placementTargetInRange = (origin: Coordinate, target: Coordinate, range: number): boolean =>
    !!target && Number.isInteger(target.x) && Number.isInteger(target.y) &&
    Math.abs(target.x - origin.x) <= range && Math.abs(target.y - origin.y) <= range

/** How far from the dropper a drag into the world can put an item, in world pixels. */
export const DROP_REACH = 3 * TileSize
const DROP_STEP = 4

/**
 * Where an item let go of at `requested` lands, for a dropper whose feet are at `origin` (both
 * fine). Within `reach` it lands on the release point itself; further out, at `reach` in the same
 * direction. Either way it stops short of the first blocked tile on the way out, so a drag can't
 * throw an item through a wall or into one. The dropper's own tile never counts as blocked: the
 * feet can stand on a tile whose footprint only fills part of it.
 *
 * Walking into a dropped item picks it up, so one let go of on the dropper's own body carries on
 * along the same line until `touchesDropper` says it is clear of them. Undefined when there is no
 * line to follow (let go of right at the feet) or no clear spot on it before a wall or the reach.
 */
export const dropTarget = (origin: Coordinate, requested: Coordinate, reach: number,
    isBlocked: (x: number, y: number) => boolean,
    touchesDropper: (at: Coordinate) => boolean = () => false): Coordinate | undefined => {
    const dx = requested.x - origin.x
    const dy = requested.y - origin.y
    const distance = Math.hypot(dx, dy)
    if (!distance) return undefined
    const length = Math.min(distance, reach)
    const start = fineToRoughCoordinates(origin)
    let landed = { x: origin.x, y: origin.y }
    let along = 0
    while (along < length || touchesDropper(landed)) {
        if (along >= reach) return undefined
        along = Math.min(along + DROP_STEP, along < length ? length : reach)
        const at = { x: origin.x + (dx / distance) * along, y: origin.y + (dy / distance) * along }
        const tile = fineToRoughCoordinates(at)
        if ((tile.x !== start.x || tile.y !== start.y) && isBlocked(tile.x, tile.y)) {
            return touchesDropper(landed) ? undefined : landed
        }
        landed = at
    }
    return landed
}
